import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ApiErrorCodes,
  parseApiResponseError,
} from '../../../../api/api-error';
import apiManager from '../../../../api/api-manager';
import {
  AggiornamentoCredenzialiError,
  AggiornamentoCredenzialiErrorCodiciErroreEnum, LoginErrorCodiciErroreEnum,
} from '../../../../api/generated/apc/api';
import { getAPIRequestHeaders } from '../../../../utils/api-utils';
import { AsyncThunkConfig } from '../../../async-thunk-config';
import {
  ChangePasswordError,
  ChangePasswordParams,
  ChangePasswordState,
} from './change-password-types';
import { verifyChangePasswordFields } from './change-password-utils';

const initialState: ChangePasswordState = {
  changePasswordError: undefined,
  changePasswordRequestStatus: 'idle',
};

export const changePassword = createAsyncThunk<
  string,
  ChangePasswordParams,
  AsyncThunkConfig<ChangePasswordError>
>('change_password', async (params: ChangePasswordParams, thunkApi) => {
  try {
    const userState = thunkApi.getState().data.user;

    const validationError = verifyChangePasswordFields(params, userState);
    if (validationError) {
      return thunkApi.rejectWithValue(validationError);
    }

    await apiManager.loginApi.cambiaPassword(
      {
        username: userState.username,
        password: params.oldPassword,
        nuovaPassword: params.newPassword,
      },
      getAPIRequestHeaders(thunkApi)
    );
    return null;
  } catch (error) {
    const apiError =
      parseApiResponseError<AggiornamentoCredenzialiError>(error);
    const errorCodes:any = apiError.payload?.codiciErrore;

    if (apiError.responseErrorCode === ApiErrorCodes.UNAUTHORIZED) {
      return thunkApi.rejectWithValue('INVALID_CREDENTIALS');
    } else if (
      errorCodes?.indexOf(
        AggiornamentoCredenzialiErrorCodiciErroreEnum.CredenzialiUsateInPrecedenza
      ) >= 0
    ) {
      return thunkApi.rejectWithValue('PASSWORD_ALREADY_USED');
    } else if (
    errorCodes?.indexOf(
      LoginErrorCodiciErroreEnum.PasswordVecchiaNonValida
    ) >= 0
  ) {
    return thunkApi.rejectWithValue('PASSWORD_VECCHIA_NON_VALIDA');
  }  else {
      return thunkApi.rejectWithValue('GENERIC_ERROR');
    }
  }
});

const changePasswordSlice = createSlice({
  name: 'features/change-password',
  initialState,
  reducers: {
    resetChangePasswordState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(changePassword.pending, (state) => {
      state.changePasswordRequestStatus = 'loading';
    });
    builder.addCase(changePassword.fulfilled, (state) => {
      state.changePasswordRequestStatus = 'succeeded';
    });
    builder.addCase(
      changePassword.rejected,
      (state, action: PayloadAction<ChangePasswordError>) => {
        state.changePasswordError = action.payload;
        state.changePasswordRequestStatus = 'failed';
      }
    );
  },
});

export const { resetChangePasswordState } = changePasswordSlice.actions;

export default changePasswordSlice.reducer;
